import React, { useState, forwardRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import InputAdornment from '@material-ui/core/InputAdornment';
import Popper from '@material-ui/core/Popper';
import _ from 'lodash';
import dayjs from 'dayjs';

import { Text, InputWithEndAdornment, SearchIcon, Avatar, NoInformation } from '../index';

import {
    StyledMessageWindowContainer,
    StyledMessageListWindow,
    StyledMessageHeader,
    StyledMessageList,
    StyledMessage,
    StyledAvatarContainer,
    StyledBadge,
    StyledMessageContainer,
    StyledTimeContainer,
    StyledMessageFooter,
} from './style';

const MessengerWindow = forwardRef(
    ({ anchorEl, data, myRole, onClick, showTitle, onSeeAll }, ref) => {
        const [searchValue, setSearchValue] = useState('');

        const open = Boolean(anchorEl);

        const messagesData = useMemo(() => {
            if (searchValue === '') {
                return data;
            } else {
                return data.filter((data) =>
                    data.patient?.user?.fullname.toLowerCase().includes(searchValue.toLowerCase())
                );
            }
        }, [data, searchValue]);

        return (
            <Popper
                open={open}
                anchorEl={anchorEl}
                placement="bottom"
                style={{ marginTop: '10px', marginBottom: '10px', zIndex: '1200' }}
                ref={ref}
            >
                <StyledMessageWindowContainer>
                    <MessageListWindow
                        showTitle={showTitle}
                        value={searchValue}
                        onChange={setSearchValue}
                        data={messagesData}
                        onClick={onClick}
                        myRole={myRole}
                        onSeeAll={onSeeAll}
                        $height="500px"
                    />
                </StyledMessageWindowContainer>
            </Popper>
        );
    }
);

MessengerWindow.propTypes = {
    anchorEl: PropTypes.object,
    data: PropTypes.arrayOf(PropTypes.object),
    myRole: PropTypes.string,
    onClick: PropTypes.func,
    showTitle: PropTypes.bool,
    onSeeAll: PropTypes.func,
};

export default MessengerWindow;

export const MessageListWindow = ({
    showTitle,
    value,
    onChange,
    data,
    onClick,
    myRole,
    $width,
    $height,
    onSeeAll,
}) => {
    return (
        <StyledMessageListWindow $width={$width} $height={$height}>
            <StyledMessageHeader>
                {showTitle && (
                    <Text $fontSize="20px" $medium style={{ marginLeft: '5px' }}>
                        Message
                    </Text>
                )}
                <InputWithEndAdornment
                    id="message-search"
                    type="text"
                    autoComplete="off"
                    multiline
                    placeholder="Search"
                    startAdornment={
                        <InputAdornment position="start">
                            <SearchIcon fill="gray12" width="16" height="16" />
                        </InputAdornment>
                    }
                    inputPropsStyle={{ paddingLeft: '5px' }}
                    $backgroundColor="gray8"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    $inputHeight="38px"
                    inputProps={{ maxLength: 500 }}
                />
            </StyledMessageHeader>
            <StyledMessageList>
                {_.isEmpty(data) ? (
                    <NoInformation />
                ) : (
                    data.map((item) => (
                        <Message key={item.id} myRole={myRole} data={item} onClick={onClick} />
                    ))
                )}
            </StyledMessageList>
            {onSeeAll && (
                <StyledMessageFooter onClick={onSeeAll}>
                    <Text $color="blue2" $fontSize="14px">
                        See all in message
                    </Text>
                </StyledMessageFooter>
            )}
        </StyledMessageListWindow>
    );
};

MessageListWindow.propTypes = {
    showTitle: PropTypes.bool,
    value: PropTypes.string,
    onChange: PropTypes.func,
    data: PropTypes.arrayOf(PropTypes.object),
    onClick: PropTypes.func,
    myRole: PropTypes.string,
    $width: PropTypes.string,
    $height: PropTypes.string,
    onSeeAll: PropTypes.func,
};

const Message = ({ data, myRole, onClick }) => {
    return (
        <StyledMessage
            isRead={data.isRead}
            onClick={() =>
                onClick({
                    id: data[myRole === 'provider' ? 'patient' : 'staff']?.id,
                    avatar: data[myRole === 'provider' ? 'patient' : 'staff']?.user?.avatar?.url,
                    name: data[myRole === 'provider' ? 'patient' : 'staff']?.user?.fullname,
                    isGroup: false,
                    messageId: data.id,
                    senderRole: data.senderRole,
                    isRead: data.isRead,
                    deactivated:
                        data[myRole === 'provider' ? 'patient' : 'staff']?.deactivated ||
                        !data[myRole === 'provider' ? 'patient' : 'staff']?.user,
                })
            }
        >
            <StyledAvatarContainer>
                {!data.isRead && <StyledBadge overlap="rectangular" />}
                <Avatar
                    src={
                        data[myRole === 'provider' ? 'patient' : 'staff']?.deactivated ||
                        !data[myRole === 'provider' ? 'patient' : 'staff']?.user
                            ? null
                            : data[myRole === 'provider' ? 'patient' : 'staff']?.user?.avatar?.url
                    }
                    alt={
                        data[myRole === 'provider' ? 'patient' : 'staff']?.deactivated ||
                        !data[myRole === 'provider' ? 'patient' : 'staff']?.user
                            ? ''
                            : data[myRole === 'provider' ? 'patient' : 'staff']?.user?.fullname
                    }
                    $avatarSize="46px"
                    $borderRadius="50%"
                />
            </StyledAvatarContainer>
            <StyledMessageContainer>
                <Text $fontSize="14px" $ellipsis $lineClamp={1} $bold={!data.isRead}>
                    {data[myRole === 'provider' ? 'patient' : 'staff']?.deactivated ||
                    !data[myRole === 'provider' ? 'patient' : 'staff']?.user
                        ? 'Inactive'
                        : data[myRole === 'provider' ? 'patient' : 'staff']?.user?.fullname}
                </Text>
                <Text
                    $fontSize="14px"
                    $ellipsis
                    $lineClamp={1}
                    $color={data.isRead ? 'gray14' : 'blue2'}
                >
                    {data.text && (data.senderRole === myRole ? `you: ${data.text}` : data.text)}
                    {data.image && (data.senderRole === myRole ? `you: new image` : 'new image')}
                    {!data.text && !data.image && 'no image'}
                </Text>
            </StyledMessageContainer>
            <StyledTimeContainer>
                <Text $fontSize="12px" $color="gray2">
                    {dayjs().isSame(dayjs(data.createdAt), 'date')
                        ? dayjs(data.createdAt).format('HH:mm')
                        : dayjs(data.createdAt).format('D/M/YY HH:mm')}
                </Text>
            </StyledTimeContainer>
        </StyledMessage>
    );
};

Message.propTypes = {
    data: PropTypes.object,
    myRole: PropTypes.string,
    onClick: PropTypes.func,
};
